import React from 'react';
import * as XLSX from "xlsx";
import * as CO from "./commons";
import * as echarts from 'echarts';
import * as H from "../../../hooks";
import * as C from "../../../Common";
import { TC, T, TB, LANG } from "../../../Constants";

type PieChartProps = {
    /** The unit of the pie data */
    unit: string;
    /** The palette of color to user */
    palette: string[];
    /** Is the component refreshing */
    refreshing?: boolean;
    /** The energy type */
    energy?: string;
    /** The color of the selected segment */
    color?: string;
    /** The data to display */
    data: ReturnType<T.API.Report.Dashboard.GetESGSectionData>["pie"];
    /** Callback to refresh the data of this component */
    refresh: (params?: Parameters<T.API.Report.Dashboard.GetESGSectionData>[0]["other"]) => void;
};

const PieChart: React.FC<PieChartProps> = ({ refresh, ...props }) => {
    const lg = H.useLanguage();
    const size = H.useWindowSize();
    const chartRef = React.useRef<HTMLDivElement>(null);
    const local_lang_value = React.useMemo(() => LANG.getHtmlLanguageCode(lg.prop), [lg.prop]);
    const [position, setPosition] = React.useState<'inside' | 'outside'>('outside');

    const excel_export = React.useCallback(() => {
        if (props.data) {
            const data = props.data.map(d => ({
                [lg.getStaticText(TC.DASH_ENV_CONFIG_GROUPING)]: d.segment,
                [lg.getStaticText(TC.GLOBAL_VALUE)]: TB.formatNumber(d.sumvalue, local_lang_value),
            }));
            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "sheet1");
            XLSX.writeFile(wb, "PieChart.xlsx");
        }
    }, [props.data]);

    React.useEffect(() => {
        if (size.width < 2000) {
            setPosition('inside');
        } else {
            setPosition('outside');
        }
    }, [size]);

    const chart_options = React.useMemo<echarts.EChartsOption>(() => ({
        legend: { type: 'scroll', width: '75%', left: 0 },
        tooltip: {
            trigger: 'item',
            formatter: params => `<table style="width:100%;">
                <tr>
                    <tr>
                        <td>${params.marker} ${params.name}</td>
                    </tr>
                    <tr>
                        <td style="text-align:left;"><strong>${TB.formatNumber(params.value, local_lang_value)}</strong> ${props.unit} </td>
                        <td style="text-align:right;"><strong> (${params.percent}%)</strong></td>
                    </tr>
                </tr>
            </table>`,
        },
        toolbox: {
            width: "20%",
            feature: {
                // dataView: { readOnly: false },
                saveAsImage: { title: lg.getStaticText(TC.NRJ_DASH_SAVE_IMAGE) },
                // myTool1: { show: true, onclick: () => refresh(), icon: CO.icons.refresh, title: lg.getStaticText(TC.GLOBAL_REFRESH) },
                myTool2: { show: true, icon: CO.icons.excel, onclick: excel_export, title: lg.getStaticText(TC.JS_PORTFOLIO_EXPORT) },
            }
        },
        series: [{
            type: 'pie',
            name: 'Consommation',
            radius: ['40%', '70%'],
            center: ['50%', '60%'],
            label: { formatter: params => params.name, position: position },
            labelLine: {
                show: true,
                length: 10, 
                length2: 10,
                lineStyle: {
                    width: 1, 
                    type: 'solid'
                }
            },
            data: props.data.map((item, index) => ({ name: item.segment, value: item.sumvalue, itemStyle: { color: props.color || props.palette[index] } })),
        }],
    }), [excel_export, refresh, lg, props.data, props.palette, props.unit, props.color, position]);

    React.useEffect(() => {
        if (chartRef.current) {
            const chartInstance = echarts.init(chartRef.current);
            chartInstance.setOption(chart_options);
            return () => chartInstance?.dispose?.();
        }
    }, [chart_options, excel_export]);

    return <div className='esg-graph d-flex flex-column w-100 h-100'>
        <C.Spinner loading={props.refreshing} min_load_size="100%">
            <div className='flex-grow-1 w-100'>
                <div ref={chartRef} className='h-100' />
            </div>
        </C.Spinner>
    </div>;
}

export default PieChart;